import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, ChevronDown, Clock, MessageCircle, Sparkles, X } from 'lucide-react';
import { whatsappLink } from './landingConfig';
import { BASE_MODULES, SPECIALTY_PAGES } from './specialtyData';
import { Mock } from './LandingMocks';
import ParticlesBackground from './ParticlesBackground';
import { Contact, FloatingWhatsApp, Footer, Header, MobileCta, onSpotlight, useReveal } from './LandingPage';
import usePageMeta from './usePageMeta';
import './Landing.css';

const SpecialtyPage = ({ spec }) => {
  useReveal();
  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://medix.cl/' },
          { '@type': 'ListItem', position: 2, name: spec.name, item: `https://medix.cl/${spec.slug}` },
        ],
      },
      ...(spec.faq.length ? [{
        '@type': 'FAQPage',
        mainEntity: spec.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      }] : []),
    ],
  }), [spec]);
  usePageMeta(`/${spec.slug}`, jsonLd);
  const [open, setOpen] = useState(0);
  const Icon = spec.icon;
  const others = SPECIALTY_PAGES.filter((s) => s.slug !== spec.slug);
  const waText = `Hola, tengo una consulta de ${spec.name.toLowerCase()} y quiero conocer Medix.`;

  useEffect(() => { window.scrollTo(0, 0); }, [spec.slug]);

  return (
    <div className="lp" id="top" onMouseMove={onSpotlight}>
      <Header />
      <main>
        <section className="lp-hero lp-hero-spec">
          <div className="lp-hero-glow" aria-hidden="true" />
          <div className="lp-aurora" aria-hidden="true"><i /><i /><i /></div>
          <ParticlesBackground />
          <div className="lp-container lp-hero-grid">
            <div className="lp-hero-copy">
              <p className="lp-pill"><Icon size={14} /> Medix para {spec.name}</p>
              <h1>{spec.title}</h1>
              <p className="lp-lead">{spec.lead}</p>
              <div className="lp-hero-actions">
                <a href="#contacto" className="lp-btn lp-btn-primary lp-btn-lg lp-shine">Solicitar piloto de 30 días <ArrowRight size={18} /></a>
                <a href={whatsappLink(waText)} className="lp-btn lp-btn-glass lp-btn-lg" target="_blank" rel="noreferrer">
                  <MessageCircle size={18} /> WhatsApp
                </a>
              </div>
              <ul className="lp-hero-checks">
                <li><Check size={16} /> Implementación en 48 horas</li>
                <li><Check size={16} /> Sin permanencia</li>
                <li><Check size={16} /> Datos en Chile</li>
              </ul>
            </div>
            <div className="lp-hero-visual lp-spec-visual">
              <Mock name={spec.mock} />
            </div>
          </div>
        </section>

        <section className="lp-section lp-section-alt">
          <div className="lp-container">
            <div className="lp-heading lp-reveal">
              <p className="lp-kicker">Lo que resolvemos</p>
              <h2>¿Te suena familiar?</h2>
            </div>
            <div className="lp-pains">
              {spec.pains.map((p, i) => (
                <article key={p} className="lp-pain lp-reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                  <span className="lp-no"><X size={16} /></span>
                  <p>{p}</p>
                </article>
              ))}
            </div>
            <p className="lp-pains-answer lp-reveal"><Sparkles size={18} /> Con Medix, todo queda en un solo sistema, ordenado y disponible para tu equipo.</p>
          </div>
        </section>

        {spec.modules.length > 0 && (
          <section className="lp-section">
            <div className="lp-container">
              <div className="lp-heading lp-reveal">
                <p className="lp-kicker">Módulos de {spec.name}</p>
                <h2>Herramientas pensadas para tu especialidad</h2>
              </div>
              <div className="lp-module-grid">
                {spec.modules.map((m, i) => (
                  <article key={m.name} className="lp-card lp-module lp-spot lp-reveal" style={{ transitionDelay: `${i * 70}ms` }}>
                    <span className="lp-module-tag">Disponible</span>
                    <h3>{m.name}</h3>
                    <p>{m.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className={spec.modules.length > 0 ? 'lp-section lp-section-alt' : 'lp-section'}>
          <div className="lp-container">
            <div className="lp-heading lp-reveal">
              <p className="lp-kicker">Incluido en todos los planes</p>
              <h2>La base que tu consulta necesita hoy</h2>
              <p className="lp-section-lead">Funciona desde el primer día, para cualquier especialidad.</p>
            </div>
            <div className="lp-module-grid">
              {BASE_MODULES.map((m, i) => (
                <article key={m.name} className="lp-card lp-module lp-spot lp-reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                  <span className="lp-check-dot"><Check size={16} /></span>
                  <h3>{m.name}</h3>
                  <p>{m.text}</p>
                </article>
              ))}
            </div>
            {spec.soon.length > 0 && (
              <div className="lp-soon lp-reveal">
                <h3><Clock size={18} /> Próximamente para {spec.name}</h3>
                <ul>{spec.soon.map((s) => <li key={s}>{s}</li>)}</ul>
              </div>
            )}
          </div>
        </section>

        <section className="lp-section lp-dark lp-plans-cta">
          <ParticlesBackground density={0.5} linkDistance={120} />
          <div className="lp-container lp-above lp-heading">
            <p className="lp-kicker">Planes</p>
            <h2>Desde $39.900 al mes + IVA</h2>
            <p className="lp-section-lead">Piloto de 30 días acompañado, implementación en 48 horas y plan mensual sin permanencia.</p>
            <div className="lp-hero-actions lp-center">
              <a href="/#planes" className="lp-btn lp-btn-primary lp-btn-lg">Ver planes y precios <ArrowRight size={18} /></a>
            </div>
          </div>
        </section>

        {spec.faq.length > 0 && (
          <section className="lp-section lp-section-alt">
            <div className="lp-container lp-faq-wrap">
              <div className="lp-heading lp-reveal">
                <p className="lp-kicker">Preguntas frecuentes</p>
                <h2>Medix para {spec.name}</h2>
              </div>
              <div className="lp-faq lp-reveal">
                {spec.faq.map((f, i) => (
                  <div key={f.q} className={open === i ? 'lp-faq-item lp-open' : 'lp-faq-item'}>
                    <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? -1 : i)}>
                      {f.q} <ChevronDown size={20} />
                    </button>
                    <div className="lp-faq-a"><p>{f.a}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <Contact title={`Veamos Medix en tu consulta de ${spec.name.toLowerCase()}`} />

        <section className="lp-section lp-other-specs">
          <div className="lp-container">
            <h2 className="lp-reveal">Otras especialidades</h2>
            <div className="lp-spec-links lp-reveal">
              {others.map((s) => (
                <a key={s.slug} href={`/${s.slug}`}><s.icon size={18} /> {s.name}</a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
      <MobileCta />
    </div>
  );
};

export default SpecialtyPage;
